import { computed, reactive } from 'vue'
import axios from 'axios'
import { load } from 'recaptcha-v3'
import { StoreBase } from '@/stores/StoreBase'
import i18n from '@/locales/I18n'
import CourseMaster from '@/stores/registerTeam/CourseMaster'
import dayjs from 'dayjs'
import APIAccessor from '@/util/APIAccesser'

class RegisterTeamStore implements StoreBase {
  /* eslint-disable class-methods-use-this */
  createStore() {
    const state = reactive({
      recaptchaToken: '',
      teamName: '',
      organizationType: process.env.VUE_APP_BUILD_TARGET === 'mbp' ? 'MBP' : 'FOOTBALL',
      apiBaseUrl: '',
      mailAddress: '',
      courseId: '',
      courseList: [] as CourseMaster[],
      planType: '',
    })

    /**
     * オフラインプラン登録かどうか
     */
    const isOfflinePlan = computed(() => {
      return state.planType === 'offline'
    })

    /**
     * ストアに格納されている値から、APIに値を送信する。
     * @see https://pitchbase.atlassian.net/wiki/spaces/SL01/pages/1101266983/API
     * @param registMethod 登録方法
     * @return 送信結果。
     */
    const registerToken = async (registMethod: 'MAIL_ADDRESS' | 'SNS_LINE') => {
      const { locale } = i18n.global
      const registTokenRes = await axios
        .post(
          `${state.apiBaseUrl}/manage/regist_token`,
          {
            reCaptchaToken: state.recaptchaToken,
            teamName: { [locale]: state.teamName },
            organizationType: state.organizationType,
            parentOrganization: state.courseList.find(course => state.courseId === course.courseId)
              ?.parentOrganization,
            courseId: state.courseId || null,
            lang: locale,
            registMethod,
            mailAddress: registMethod === 'MAIL_ADDRESS' ? state.mailAddress : null,
            additionalData: isOfflinePlan.value ? { offlinePlan: true } : null,
          },
          {
            headers: {
              'Content-Type': 'application/json',
            },
          },
        )
        .then(res => {
          return { isSuccess: true, res }
        })
        .catch(error => {
          let errRes = error
          // ネットワークエラー対策
          if (!errRes.response) {
            // eslint-disable-next-line @typescript-eslint/camelcase
            errRes = { response: { data: { error_code: 'NETWORK_ERROR' } } }
          }
          return { isSuccess: false, res: errRes }
        })
      return registTokenRes
    }

    /**
     * ユーザのメールアドレス重複確認APIにより登録可能なメールアドレスか確認する。
     * @see https://pitchbase.atlassian.net/wiki/spaces/SL01/pages/1560641641/API
     * @param {string} baseUrl チェック対象のバックエンドサーバーのBASE_URL
     * @param {string} recaptchaToken チェックに用いるrecaptchaToken
     * @return 送信結果。
     */
    const checkDuplicateMailAddress = async (baseUrl: string, recaptchaToken: string) => {
      const result = await axios
        .get(
          `${baseUrl}/manage/service_registration/validate/mail_address/${state.mailAddress}?reCaptchaToken=${recaptchaToken}`,
          {
            headers: {
              'Content-Type': 'application/json',
            },
          },
        )
        .then(res => {
          return { isSuccess: true, res }
        })
        .catch(error => {
          let errRes = error
          // ネットワークエラー対策
          if (!errRes.response) {
            // eslint-disable-next-line @typescript-eslint/camelcase
            errRes = { response: { data: { error_code: 'NETWORK_ERROR' } } }
          }
          return { isSuccess: false, res: errRes.response }
        })
      if (result.res.data.error_code === '409_0000')
        return {
          ...result,
          /**
           * メールアドレスが重複しているかどうか
           */
          duplicate: true,
        }

      return {
        ...result,
        /**
         * メールアドレスが重複しているかどうか
         */
        duplicate: false,
      }
    }

    /**
     * reCaptchaトークンを取得する
     * 取得したtokenはストアに保存される
     * @param action トークン取得時に指定するAction
     * @return トークン
     */
    const getReCaptchaToken = async (action = 'getToken') => {
      state.recaptchaToken = ''
      try {
        const recaptcha = await load(process.env.VUE_APP_RECAPTCHA_SITE_KEY)
        const token = await recaptcha.execute(action)
        state.recaptchaToken = token
        return token
      } catch {
        state.recaptchaToken = ''
        return ''
      }
    }

    /**
     * 定義済みの全リージョンに対して
     * reCaptchaのトークンを取得し、ストアに格納されている値で
     * ユーザのメールアドレス重複確認APIを実行する
     * APIの仕様に関しては以下を参照する
     * @see https://pitchbase.atlassian.net/wiki/spaces/SL01/pages/1560641641/API
     * @return {Object} メールアドレスが重複しているかどうかを返す。チェックに成功し、メールアドレスが重複していない場合は
     * isValidMainAddressの項目がtrueになる
     */
    const checkDuplicateMailAllRegion = async () => {
      const urls: Record<string, string> = JSON.parse(process.env.VUE_APP_ALL_API_BASE_URL ?? '{}')
      const res = await Promise.all(
        Object.values(urls).map(async url => {
          const recaptchaToken = await getReCaptchaToken()
          if (!recaptchaToken) {
            return {
              isSuccess: false,
              duplicate: false,
            }
          }
          return checkDuplicateMailAddress(url, recaptchaToken)
        }),
      )
      const isSuccess = res.every(r => r.isSuccess)
      const isDuplicateMail = res.some(r => r.duplicate)
      return {
        /**
         * チェックが成功したかどうか
         */
        isSuccess,
        /**
         * メールが重複しているかどうか
         */
        isDuplicateMail,
        /**
         * メールが使用可能かどうか
         */
        isValidMainAddress: isSuccess && !isDuplicateMail,
      }
    }

    /**
     * reCaptchaのトークンを取得し、ストアに格納されている値で
     * ユーザー登録リクエストAPIを実行する
     * APIの仕様に関しては以下を参照する
     * @see https://pitchbase.atlassian.net/wiki/spaces/SL01/pages/1101266983/API
     * @param registMethod 登録方法
     *
     */
    const getTokenAndSendCreateUserRequest = async (registMethod: 'MAIL_ADDRESS' | 'SNS_LINE') => {
      await getReCaptchaToken()
      if (!state.recaptchaToken)
        // eslint-disable-next-line @typescript-eslint/camelcase
        return { isSuccess: false, res: { response: { data: { error_code: 'NETWORK_ERROR' } } } }
      return registerToken(registMethod)
    }

    /**
     * コースの一覧を取得する。
     */
    const fetchCourseList = async () => {
      await getReCaptchaToken()
      if (!state.recaptchaToken)
        // eslint-disable-next-line @typescript-eslint/camelcase
        return { isSuccess: false, res: { response: { data: { error_code: 'NETWORK_ERROR' } } } }

      const courseListRes = await APIAccessor.request({
        url: `${state.apiBaseUrl}/data/master/course`,
        method: 'GET',
        query: {
          reCaptchaToken: state.recaptchaToken,
          filter: {
            $and: [
              { startDate: { $lt: dayjs().valueOf() } },
              { _deletedFlag: false },
              {
                $or: [{ endDate: { $exists: false } }, { endDate: { $gt: dayjs().valueOf() } }],
              },
            ],
          },
        },
      })
        .then(res => {
          return { isSuccess: true, res }
        })
        .catch(error => {
          let errRes = error
          // ネットワークエラー対策
          if (!errRes.response) {
            // eslint-disable-next-line @typescript-eslint/camelcase
            errRes = { response: { data: { error_code: 'NETWORK_ERROR' } } }
          }
          return { isSuccess: false, res: errRes }
        })

      if (courseListRes.isSuccess) {
        const results = courseListRes.res.data as Partial<CourseMaster>[]
        state.courseList = results.map(data => new CourseMaster(data))
      }
      return courseListRes
    }

    return {
      getTokenAndSendCreateUserRequest,
      checkDuplicateMailAllRegion,
      fetchCourseList,
      isOfflinePlan,
      /**
       * プランタイプ
       * @param planType プランタイプ
       */
      set planType(planType: string) {
        state.planType = planType
      },
      /**
       * recaptcha-v3 Token
       */
      get recaptchaToken(): string {
        return state.recaptchaToken
      },
      /**
       * recaptcha-v3 Token
       * @param recaptchaToken recaptcha-v3 Token
       */
      set recaptchaToken(recaptchaToken: string) {
        state.recaptchaToken = recaptchaToken
      },
      /**
       * 組織情報タイプ
       */
      get organizationType(): string {
        return state.organizationType
      },
      /**
       * 組織情報タイプ
       * @param organizationType 組織情報
       */
      set organizationType(organizationType: string) {
        state.organizationType = organizationType
      },
      /**
       * 登録サーバー
       */
      get apiBaseUrl(): string {
        return state.apiBaseUrl
      },
      /**
       * 登録サーバー
       * @param apiBaseUrl API Base URL
       */
      set apiBaseUrl(apiBaseUrl: string) {
        state.apiBaseUrl = apiBaseUrl
      },
      /**
       * チーム名
       */
      get teamName(): string {
        return state.teamName
      },
      /**
       * チーム名
       * @param teamName チーム名
       */
      set teamName(teamName: string) {
        state.teamName = teamName
      },
      /**
       * メールアドレス
       */
      get mailAddress() {
        return state.mailAddress
      },
      /**
       * メールアドレス
       * @param address メールアドレス
       */
      set mailAddress(address: string) {
        state.mailAddress = address
      },
      /**
       * ステップ２にて、ライン登録を表示する場合には、trueを返す
       * FLUXBACK-2532 の恒久対応（認可コードをサーバーで交換する形への移行）まで LINE 登録を停止しているため、常にfalseを返す。
       * 停止前は、ステップ１で入力した地域が日本（判定できない場合を含む）ならtrueを返していた。
       */
      get isLineRegistrationVisible() {
        return false
      },
      /**
       * コースID
       */
      get courseId(): string {
        return state.courseId
      },
      /**
       * コースID
       * @param courseId コースID
       */
      set courseId(courseId: string) {
        state.courseId = courseId
      },
      /**
       * コース一覧
       */
      get courseList(): CourseMaster[] {
        return state.courseList
      },
      /**
       * コース一覧
       * @param courseList コース一覧
       */
      set courseList(courseList: Array<CourseMaster>) {
        state.courseList = courseList
      },
    }
  }

  /* eslint-enable class-methods-use-this */
}

export default new RegisterTeamStore().createStore()
